(() => {
  if (customElements.get('fxl-lens-showcase')) return;
  class FxlLensShowcase extends HTMLElement {
    connectedCallback() {
      if (this.ready) { this.observeLayout(); return; }
      this.ready = true;
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[data-showcase-panel]')];
      this.querySelector('[role="tablist"]').hidden = this.tabs.length < 2;
      this.panels.forEach((panel, index) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', this.tabs[index].id);
        panel.querySelector('[data-showcase-cta]')?.addEventListener('click', event => {
          if (panel.getAttribute('aria-busy') === 'true') event.preventDefault();
        });
        panel.querySelector('[data-showcase-toggle]')?.addEventListener('click', () => {
          panel.colorsExpanded = !panel.colorsExpanded;
          this.updateColors();
        });
        panel.querySelectorAll('[data-showcase-swatch]').forEach((swatch, i) => {
          swatch.setAttribute('role', 'button');
          swatch.setAttribute('aria-pressed', String(i === 0));
          swatch.addEventListener('click', event => { event.preventDefault(); this.selectColor(panel, swatch); });
          swatch.addEventListener('keydown', event => {
            if (event.key === ' ') { event.preventDefault(); swatch.click(); }
          });
        });
      });
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectTab(index));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') next = (index + this.tabs.length - 1) % this.tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.tabs.length - 1;
          if (next === undefined) return;
          event.preventDefault(); this.selectTab(next); this.tabs[next].focus();
        });
      });
      this.selectTab(0);
      this.setAttribute('data-ready', '');
      this.observeLayout();
      window.MRYLensSwatch?.hydrate(this);
    }
    observeLayout() {
      this.layoutObserver?.disconnect();
      this.layoutObserver = new ResizeObserver(() => this.updateColors());
      this.panels.forEach(panel => {
        const grid = panel.querySelector('.fxl-lens-showcase__swatches');
        if (grid) this.layoutObserver.observe(grid);
      });
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.onLayoutChange = () => this.updateColors();
      this.mobileQuery.addEventListener('change', this.onLayoutChange);
      this.updateColors();
    }
    disconnectedCallback() {
      this.layoutObserver?.disconnect();
      this.mobileQuery?.removeEventListener('change', this.onLayoutChange);
    }
    updateColors() {
      this.panels.forEach(panel => {
        if (panel.hidden) return;
        const grid = panel.querySelector('.fxl-lens-showcase__swatches');
        const toggle = panel.querySelector('[data-showcase-toggle]');
        if (!grid || !toggle || !grid.clientWidth) return;
        const swatches = [...grid.querySelectorAll('[data-showcase-swatch]')];
        const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
        const columns = Math.max(1, Math.floor((grid.clientWidth + gap) / (44 + gap)));
        const compact = window.matchMedia('(max-width: 749px)').matches && swatches.length > columns * 2;
        swatches.forEach((swatch, i) => { swatch.hidden = compact && !panel.colorsExpanded && i >= columns * 2; });
        const selected = swatches.findIndex(swatch => swatch.getAttribute('aria-pressed') === 'true');
        if (compact && !panel.colorsExpanded && selected >= columns * 2) {
          swatches[columns * 2 - 1].hidden = true;
          swatches[selected].hidden = false;
        }
        toggle.hidden = !compact;
        toggle.setAttribute('aria-expanded', String(!!panel.colorsExpanded));
        toggle.querySelector('[data-showcase-toggle-label]').textContent = panel.colorsExpanded ? 'Show fewer colors' : `Show all ${swatches.length} colors`;
      });
    }
    selectTab(index) {
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
      this.updateColors();
      window.MRYLensSwatch?.hydrate(this.panels[index]);
    }
    setBusy(panel, busy) {
      if (busy) panel.setAttribute('aria-busy', 'true');
      else panel.removeAttribute('aria-busy');
      panel.querySelector('[data-showcase-cta]')?.setAttribute('aria-disabled', String(busy));
    }
    async selectColor(panel, swatch) {
      // Only the latest click may commit the image, label and product URL together.
      const token = {};
      panel.request = token;
      if (swatch.getAttribute('aria-pressed') === 'true') {
        this.setBusy(panel, false);
        const status = panel.querySelector('[data-showcase-status]');
        status.textContent = '';
        status.removeAttribute('data-error');
        return;
      }
      this.setBusy(panel, true);
      const status = panel.querySelector('[data-showcase-status]');
      const current = panel.querySelector('[data-showcase-image]');
      status.removeAttribute('data-error');
      status.textContent = `Loading ${swatch.dataset.color}`;
      const next = new Image();
      next.sizes = current.sizes;
      next.srcset = swatch.dataset.srcset;
      next.src = swatch.dataset.image;
      let timeout;
      try {
        await Promise.race([next.decode(), new Promise((_, reject) => { timeout = setTimeout(() => reject(new Error('timeout')), 12000); })]);
        if (panel.request !== token) return;
        current.srcset = swatch.dataset.srcset;
        current.src = swatch.dataset.image;
        current.alt = swatch.dataset.alt;
        panel.querySelector('[data-showcase-color]').textContent = swatch.dataset.color;
        panel.querySelector('[data-showcase-type]').textContent = swatch.dataset.lensType;
        panel.querySelector('[data-showcase-cta]').href = swatch.href;
        panel.querySelectorAll('[data-showcase-swatch]').forEach(button => button.setAttribute('aria-pressed', String(button === swatch)));
        status.textContent = '';
        this.updateColors();
      } catch {
        if (panel.request === token) {
          status.setAttribute('data-error', '');
          status.textContent = 'Preview could not load. Please try again.';
        }
      } finally {
        clearTimeout(timeout);
        if (panel.request === token) this.setBusy(panel, false);
      }
    }
  }
  customElements.define('fxl-lens-showcase', FxlLensShowcase);
  document.addEventListener('mry:lens-swatches-ready', () => document.querySelectorAll('fxl-lens-showcase').forEach(el => window.MRYLensSwatch?.hydrate(el)));
})();
